import { Luach } from '@app/luach'
import { type ReactNode, useState } from 'react'
import { Badge, Card } from '../../../ui'
import { SegmentedControl } from '../../../ui/SegmentedControl'
import { TableCard } from '../../../ui/Table'
import {
  DEMO_CAMPAIGNS,
  DEMO_MESSAGE,
  DEMO_MESSAGE_RESOLVED,
  DEMO_OUTSTANDING,
  DEMO_SCHEDULED,
  DEMO_STAFF,
} from '../demo/office'
import { DEMO_HOUSEHOLDS, DEMO_YAHRZEITS } from '../demo/people'
import { DEMO_SCHEDULE, DEMO_SHUL } from '../demo/schedule'

/**
 * The product shots.
 *
 * Every one of these renders the app's OWN components against an invented
 * congregation — never an image. An image of the product is a second copy of
 * the design, and the second copy is the one that goes stale: the day a table
 * gains a rule or a badge loses its fill, a screenshot starts lying and nothing
 * fails to tell us. These cannot drift, because they are the thing they depict.
 *
 * The constraint that follows: a shot may only use components that render from
 * props. Nothing here reaches for a query, and no app screen is refactored to
 * be shot-able — where a screen's markup is tangled with its data, the answer
 * is a different shot, not a refactor of working code.
 */

/** The frame. A titled bar, the running component, and a caption saying what
 *  you are looking at — so a shot reads as the product rather than as artwork
 *  belonging to this page. */
export function Shot({
  crumb,
  note,
  caption,
  clipped,
  children,
}: {
  crumb: string
  note?: string
  caption?: string
  /** Fades a long artifact off at the bottom, for something meant to be
   *  recognized rather than read end to end. */
  clipped?: boolean
  children: ReactNode
}) {
  return (
    <figure className="mk-shot">
      <div className="mk-shot__bar">
        <span className="mk-shot__crumb">{crumb}</span>
        {note ? <span className="mk-shot__note">{note}</span> : null}
      </div>
      <div
        className={`mk-shot__stage${clipped ? ' mk-shot__stage--clip' : ''}`}
      >
        {children}
      </div>
      {caption ? (
        <figcaption className="mk-shot__cap">{caption}</figcaption>
      ) : null}
    </figure>
  )
}

const money = (cents: number) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`

/* ── The sheet ────────────────────────────────────────────────────────────── */

/**
 * The luach, rendered by the same component that prints the PDF and serves the
 * public page. `.luach-frame` is the type scope the app and the print sheet
 * both put it in, so what a visitor sees here is what comes out of the printer.
 */
export function LuachShot() {
  return (
    <Shot
      crumb="Shabbos · Parshas Vayeira"
      note="Sample congregation"
      caption="The same sheet that prints, goes on the wall, and answers the phone."
      clipped
    >
      <div className="luach-frame">
        <Luach
          result={DEMO_SCHEDULE}
          shul={DEMO_SHUL}
          options={{ showSubtitle: true, showCivilDate: true, columns: 1 }}
        />
      </div>
    </Shot>
  )
}

/* ── Members ──────────────────────────────────────────────────────────────── */

export function MembersShot() {
  return (
    <Shot crumb="Members" note="Sample congregation">
      <TableCard compact>
        <table>
          <thead>
            <tr>
              <th>Household</th>
              <th>Head</th>
              <th>People</th>
              <th>Member since</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_HOUSEHOLDS.map((h) => (
              <tr key={h.name}>
                {/* One family for the pair — Plex carries both scripts, so a
                    name beside its Hebrew is not split across two faces. */}
                <td className="mk-bi">
                  {h.name}
                  <span className="mk-bi__he" dir="rtl">
                    {h.nameHe}
                  </span>
                </td>
                <td>{h.head}</td>
                <td className="mk-num">{h.members}</td>
                <td className="mk-num">{h.since}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </Shot>
  )
}

export function YahrzeitShot() {
  return (
    <Shot
      crumb="Yahrzeits · coming up"
      caption="Kept on the Hebrew date, so it lands on the right day every year without anyone moving it."
    >
      <TableCard compact>
        <table>
          <thead>
            <tr>
              <th>For</th>
              <th>Hebrew date</th>
              <th>This year</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_YAHRZEITS.map((y) => (
              <tr key={y.forWhom}>
                <td>{y.forWhom}</td>
                <td className="mk-he" dir="rtl">
                  {y.hebrew}
                </td>
                <td className="mk-num">{y.civil}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </Shot>
  )
}

/* ── The schedule ─────────────────────────────────────────────────────────── */

/**
 * A davening time as the shul actually holds it: what it hangs off, and why.
 * The reason is the part no spreadsheet has room for, so it is given the same
 * weight as the number rather than tucked underneath it.
 *
 * These are the shul's REAL figures and its real reason — `mincha_shabbos` at
 * shkia−40 and `mincha_yt` at shkia−20, both explained by whether people go home
 * to eat. An invented example stood here before and got the domain wrong: it
 * attached seudah shlishis, which is a Shabbos-AFTERNOON meal, to erev Shabbos
 * mincha on a Friday, where it has no bearing at all.
 *
 * Worth keeping in mind for whatever replaces this next: a made-up rule on a page
 * arguing about correctness is the one error the argument cannot survive, and it
 * is invisible to everyone who does not already know the halacha.
 */
export function RuleShot() {
  return (
    <Shot
      crumb="Schedule · Mincha"
      caption="Change the reason and the times follow. Nobody retypes a column."
    >
      <Card className="mk-rule">
        <div className="mk-rule__head">
          <span className="mk-rule__name">Mincha</span>
          <Badge>Every week</Badge>
        </div>
        <p className="mk-rule__formula">
          40 minutes before <em>shkia</em>
        </p>
        <dl className="mk-rule__rows">
          <div>
            <dt>Except</dt>
            <dd>
              on Yom Tov — <strong>20 minutes before</strong>
            </dd>
          </div>
          <div>
            <dt>Because</dt>
            <dd>
              On Shabbos people go home afterwards to eat seudah shlishis. On
              Yom Tov they don’t, so mincha sits closer to shkia.
            </dd>
          </div>
          <div>
            <dt>Prints on</dt>
            <dd>The weekly sheet, the lobby screen, the Friday message</dd>
          </div>
        </dl>
      </Card>
    </Shot>
  )
}

/**
 * The same time, in every place it is published.
 *
 * This is the drift shot. Kept as schedules, a time lives on the sheet, on the
 * board and in the message, and keeping three copies in step is done by copying,
 * exporting and uploading — so the interesting question is not whether they
 * agree today but which one is wrong by next month. Kept as a rule, there are no
 * copies: each of these READS it, and disagreeing is not something they can do.
 *
 * So the shot deliberately shows one number three times. That repetition IS the
 * argument, and it is why the three panels are shown together rather than as
 * three separate shots down the page.
 */
export function OutputsShot() {
  return (
    <Shot
      crumb="One rule · three places it appears"
      caption="Nothing here was copied from anything else. All three read the same rule."
    >
      <div className="mk-outs">
        <Card className="mk-out" tone="sunken">
          <p className="mk-out__where">Printed sheet</p>
          <div className="mk-out__sheet">
            <span className="mk-out__he" dir="rtl">
              מנחה וקבלת שבת
            </span>
            <span className="mk-out__time">4:06</span>
          </div>
          <div className="mk-out__sheet">
            <span className="mk-out__he" dir="rtl">
              הדלקת נרות
            </span>
            <span className="mk-out__time">4:28</span>
          </div>
        </Card>

        <Card className="mk-out" tone="sunken">
          <p className="mk-out__where">The board in the lobby</p>
          <div className="mk-out__board">
            <span className="mk-out__board-label">Mincha</span>
            <span className="mk-out__board-time">4:06</span>
          </div>
        </Card>

        <Card className="mk-out" tone="sunken">
          <p className="mk-out__where">Friday text message</p>
          <p className="mk-out__msg">
            Good Shabbos. Mincha is at <strong>4:06</strong> and candle lighting
            is <strong>4:28</strong>.
          </p>
        </Card>
      </div>
    </Shot>
  )
}

/**
 * The year's own decisions, shown as answers rather than settings. Nobody ticks
 * these — the calendar does, and the sheet follows.
 */
export function YearShot() {
  /*
   * Every row is a real year, checked against the engine — see the fall-pattern
   * query behind the exhibit. The table said "Tisha B'Av 5786 · Falls on
   * Shabbos" and 5786's is a Thursday; the Shabbos years are 5789 and 5792. It
   * also listed Asara B'Teves, which has no occasion in the seed at all, so the
   * sheet it implies could not be produced.
   *
   * A row here is a promise that the engine handles that year. Inventing one is
   * the same error as inventing a rule.
   */
  const rows = [
    ['Pesach 5786', 'Runs into Shabbos', 'Eruv tavshilin on the sheet'],
    ['Shavuos 5787', 'Starts Friday', 'Second day is Shabbos'],
    ['Tisha B’Av 5789', 'Falls on Shabbos', 'Fast pushed to Sunday'],
    ['Adar 5787', 'Leap year', 'Purim in the second Adar'],
  ]
  return (
    <Shot
      crumb="This year"
      caption="Nobody chooses these. The year does, and the sheet is built to match."
    >
      <TableCard compact>
        <table>
          <thead>
            <tr>
              <th>Occasion</th>
              <th>How it falls</th>
              <th>What changes</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([what, how, then]) => (
              <tr key={what}>
                <td>{what}</td>
                <td>{how}</td>
                <td className="mk-strong">{then}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </Shot>
  )
}

/* ── Messaging ────────────────────────────────────────────────────────────── */

export function MessageShot() {
  return (
    <Shot
      crumb="Messaging · new message"
      note={`${DEMO_MESSAGE.recipients} recipients`}
    >
      <Card className="mk-msg">
        <p className="mk-msg__subject">{DEMO_MESSAGE.subject}</p>
        <p className="mk-msg__to">{DEMO_MESSAGE.audience}</p>
        <pre className="mk-msg__body">{DEMO_MESSAGE.body}</pre>
        <div className="mk-msg__resolved">
          <p className="mk-msg__resolved-label">Goes out as</p>
          {DEMO_MESSAGE_RESOLVED.map((r) => (
            <p className="mk-msg__pair" key={r.token}>
              <code>{r.token}</code>
              <span>{r.value}</span>
            </p>
          ))}
        </div>
      </Card>
    </Shot>
  )
}

export function ScheduledShot() {
  return (
    <Shot
      crumb="Messaging · scheduled"
      caption="Set against the occasion, not a date — so it is still right next year."
    >
      <TableCard compact>
        <table>
          <thead>
            <tr>
              <th>Message</th>
              <th>Goes out</th>
              <th>Next</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_SCHEDULED.map((s) => (
              <tr key={s.name}>
                <td className="mk-strong">{s.name}</td>
                <td>{s.when}</td>
                <td className="mk-num">{s.next}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </Shot>
  )
}

/**
 * A send that is pinned to an occasion, and the weeks it decides to hold back.
 *
 * The held-back row is the whole point of the shot. Anything can send a message
 * every Friday; what a shul needs is a scheduler that knows the Friday it should
 * NOT — because that Shabbos is Yom Tov and a different sheet applies. Showing
 * only the sends would demonstrate a cron job.
 *
 * The dates are deliberately relative rather than civil. This page argues about
 * correctness, so inventing a specific date nobody has checked would be the one
 * kind of mistake it cannot afford.
 */
export function SendRulesShot() {
  const runs = [
    { when: 'This Friday', at: '2:29 PM', held: null },
    { when: 'Next Friday', at: '2:26 PM', held: null },
    {
      when: 'The Friday of Pesach',
      at: null,
      held: 'That Shabbos is Yom Tov — the Yom Tov sheet goes instead',
    },
    { when: 'The Friday after', at: '6:41 PM', held: null },
  ]
  return (
    <Shot
      crumb="Scheduled · weekly zmanim"
      caption="Set once. It works out every send from then on, including the ones it should skip."
    >
      <Card className="mk-send">
        <dl className="mk-send__rules">
          <div>
            <dt>Goes out</dt>
            <dd>Every Friday, two hours before candle lighting</dd>
          </div>
          <div>
            <dt>Never</dt>
            <dd>On Shabbos or Yom Tov</dd>
          </div>
        </dl>
        <div className="mk-send__runs">
          {runs.map((r) => (
            <div
              className={`mk-send__run${r.held ? ' mk-send__run--held' : ''}`}
              key={r.when}
            >
              <span className="mk-send__when">{r.when}</span>
              {r.at ? (
                <span className="mk-send__at">{r.at}</span>
              ) : (
                <span className="mk-send__why">{r.held}</span>
              )}
            </div>
          ))}
        </div>
      </Card>
    </Shot>
  )
}

/**
 * An invitation, and what the office got back from it.
 *
 * ## The output here is the real output
 *
 * The subject, the email body and the text below are what `agent.readInvitation`
 * plus `simchaSubject` / `simchaBody` / `simchaSms` actually produce, taken from
 * a run against a real invitation and then renamed. An earlier version of this
 * shot invented a tidy prose email — "the families invite the kehilla to…" —
 * which was not merely different wording but the wrong claim: the product does
 * NOT paraphrase. It keeps the family's own words, in the family's own order,
 * because an invitation is how somebody chose to say something and rewriting it
 * is not ours to do.
 *
 * That is why only the LEFT side is styled. The invitation is set like an
 * invitation, since its typography is the thing being read past; the output is
 * left plain, because plain is what it is. Dressing the output would suggest we
 * designed it, when the point is that we preserved it.
 *
 * The invitation is type rather than an image — an image of a real invitation is
 * exactly what must never be in this repo, and every name here is invented
 * (`15-security.md`). It stands in for the photograph somebody uploads.
 */
export function SimchaShot() {
  /*
   * Which of the two composed outputs is on show.
   *
   * A toggle rather than both at once, because stacking the email and the text
   * made our column two or three times the height of theirs — and a comparison
   * where one side towers over the other stops reading as a comparison. They are
   * also alternatives in reality: a member gets one or the other.
   */
  const [channel, setChannel] = useState<'email' | 'sms'>('email')

  return (
    <Shot
      crumb="Simcha · from an invitation"
      caption="It reads the photograph, keeps the family's wording, and writes the subject — which the invitation does not carry."
    >
      <div className="mk-simcha">
        <div className="mk-simcha__side">
          <p className="mk-out__where mk-simcha__head">
            What came off the photograph
          </p>
          {/* The upload's own casing, and no typography at all. The invitation
              sets its display lines in caps, so that is what the words look
              like; treating them here would blur the one comparison the shot
              exists to make. */}
          <p className="mk-simcha__read">
            WITH GRATITUDE TO HASHEM PLEASE JOIN US FOR THE VORT OF OUR CHILDREN
            {'\n'}RIVKY{'\n'}and{'\n'}SHIA{'\n'}AT ONE O’CLOCK IN THE AFTERNOON
            {'\n'}14 SYCAMORE COURT • LAKEWOOD{'\n'}FRIDAY | JULY 24{'\n'}Shimon
            &amp; Leah Aschner{'\n'}Moshe &amp; Gitty Weisbrod
          </p>
        </div>

        <div className="mk-simcha__side">
          {/* The control leads: it governs everything below it, including
              whether there is a subject at all. It sits in the head slot so it
              shares a row with the label opposite, which is what brings the two
              boxes flush at the top. */}
          <div className="mk-simcha__switch mk-simcha__head">
            <SegmentedControl
              ariaLabel="Which version to show"
              size="sm"
              value={channel}
              onChange={setChannel}
              options={[
                { value: 'email', label: 'Email' },
                { value: 'sms', label: 'Text' },
              ]}
            />
          </div>

          {channel === 'email' ? (
            /*
             * The email as `simchaBodyHtml` now sets it: the family's lines in
             * their order, centered, names large, the join between them small and
             * italic, hosts italic under a hairline.
             *
             * Every treatment here exists in the renderer — this depicts real
             * output rather than running ahead of it. The arrow points that way
             * round: the product leads and the page follows.
             */
            <div className="mk-simcha__mail">
              {/* The subject inside the box, ruled off from the body — which is
                  where it sits in a mail client, and it stops the box starting
                  lower than the one opposite. Composed, not extracted: the
                  invitation carries no subject line, and a text has none either,
                  which is why it appears in this branch only. */}
              <span className="mk-simcha__mail-subject">
                Vort — Rivky and Shia (Aschner · Weisbrod)
              </span>
              <span className="mk-simcha__mail-line">
                With gratitude to Hashem please join us for the vort of our
                children
              </span>
              <span className="mk-simcha__mail-name mk-simcha__mail-name--first">
                Rivky
              </span>
              <span className="mk-simcha__mail-join">and</span>
              <span className="mk-simcha__mail-name mk-simcha__mail-name--last">
                Shia
              </span>
              <span className="mk-simcha__mail-when">
                At one o’clock in the afternoon
              </span>
              <span className="mk-simcha__mail-when mk-simcha__mail-when--tight">
                14 Sycamore Court • Lakewood
              </span>
              <span className="mk-simcha__mail-when">Friday, July 24</span>
              <span className="mk-simcha__mail-rule" aria-hidden />
              <span className="mk-simcha__mail-host">
                Shimon &amp; Leah Aschner
              </span>
              <span className="mk-simcha__mail-host">
                Moshe &amp; Gitty Weisbrod
              </span>
            </div>
          ) : (
            /* One fact per line, and plain — a text is read on a lock screen, in
               a list, between other things. Boxed like the email, because the
               two are the same panel in two states and a bordered version next
               to a bare one reads as one of them being unfinished. */
            <p className="mk-simcha__sms">
              Vort: Rivky and Shia (Aschner · Weisbrod){'\n'}Friday, July 24 ·
              At one o’clock in the afternoon{'\n'}14 Sycamore Court • Lakewood
              {'\n'}Shimon &amp; Leah Aschner{'\n'}Moshe &amp; Gitty Weisbrod
            </p>
          )}
        </div>

        <div className="mk-simcha__judged">
          <p className="mk-out__where">What it had to work out</p>
          <dl className="mk-simcha__rows">
            <div>
              <dt>Who is hosting</dt>
              <dd>
                Both couples in the script line, because the invitation says{' '}
                <em>our children</em> — so they are the parents, not the couple.
                A further line would have been grandparents, who are named but
                are not hosting.
              </dd>
            </div>
            <div>
              <dt>The caps</dt>
              <dd>
                The invitation is set in caps throughout, and it works there
                because it is display typography — chosen, laid out, printed.
                Sent as a text message the same words arrive as shouting, with
                no typesetting to carry them. So all of it comes back in
                sentence case: the greeting, the time, the address and the
                names.
              </dd>
            </div>
            <div>
              <dt>The subject</dt>
              <dd>
                Both first names and both family names. First names alone mean
                nothing in an inbox, and a vort belongs to two families.
              </dd>
            </div>
            <div>
              <dt>The script, and Hebrew</dt>
              <dd>
                The hosts are the hardest line on the page to read, and on most
                invitations the Hebrew is — which is where text recognition
                usually gives up.
              </dd>
            </div>
            <div>
              <dt>What it did not do</dt>
              <dd>
                The invitation gives no year, so none was recorded and none was
                worked out. Nothing was marked unreadable either, because
                “absent” and “I couldn’t make it out” are different answers.
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </Shot>
  )
}

/**
 * What somebody typed, and what the shul had a moment later.
 *
 * The pairing is the argument: a sentence a gabbai would say out loud on one
 * side, and on the other the three separate things it turned into — the timing,
 * the exception, and the reason. Depth normally costs a setup; this is the shot
 * that says why it doesn't here.
 */
export function AssistantShot() {
  return (
    <Shot
      crumb="Assistant · setting up a rule"
      caption="It proposes; you confirm. Nothing reaches your shul before you have seen it."
    >
      <div className="mk-asst">
        <Card className="mk-asst__said" tone="sunken">
          <p className="mk-out__where">What you type</p>
          <p className="mk-asst__quote">
            “Mincha is forty minutes before shkia, but twenty on Yom Tov,
            because on Shabbos people go home afterwards to eat seudah
            shlishis.”
          </p>
        </Card>
        <Card className="mk-asst__got">
          <p className="mk-out__where">What it writes</p>
          <dl className="mk-rule__rows">
            <div>
              <dt>Ordinarily</dt>
              <dd className="mk-asst__code">40 minutes before shkia</dd>
            </div>
            <div>
              <dt>Except</dt>
              <dd className="mk-asst__code">Yom Tov → 20 minutes before</dd>
            </div>
            <div>
              <dt>Because</dt>
              <dd>
                On Yom Tov nobody goes home for seudah shlishis, so mincha sits
                closer to shkia.
              </dd>
            </div>
          </dl>
        </Card>
      </div>
    </Shot>
  )
}

/* ── Money ────────────────────────────────────────────────────────────────── */

export function OutstandingShot() {
  return (
    <Shot crumb="Billing · outstanding" note="Sample congregation">
      <TableCard compact>
        <table>
          <thead>
            <tr>
              <th>Household</th>
              <th>Charge</th>
              <th>Owed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_OUTSTANDING.map((r) => {
              const owed = r.cents - r.paid
              return (
                <tr key={`${r.household}-${r.charge}`}>
                  <td>{r.household}</td>
                  <td>{r.charge}</td>
                  <td className="mk-num">{owed > 0 ? money(owed) : '—'}</td>
                  <td>
                    {/* A state does not earn a color by existing: paid, part
                        paid and unpaid are all the neutral chip. */}
                    <Badge>
                      {owed === 0
                        ? 'Paid'
                        : r.paid > 0
                          ? 'Part paid'
                          : 'Unpaid'}
                    </Badge>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </TableCard>
    </Shot>
  )
}

export function CampaignsShot() {
  return (
    <Shot
      crumb="Campaigns"
      caption="Every gift lands against the appeal it was given for, wherever it came from."
    >
      <div className="mk-camps">
        {DEMO_CAMPAIGNS.map((c) => (
          <Card className="mk-camp" key={c.name}>
            <p className="mk-camp__name">{c.name}</p>
            <p className="mk-camp__figure">{money(c.pledgedCents)}</p>
            <p className="mk-camp__of">
              pledged of {money(c.goalCents)} · {c.gifts} gifts
            </p>
            <span
              className="mk-camp__bar"
              style={{
                // The one inline value on the page: a proportion is data, not
                // a style choice, and there is no token for "62% along".
                ['--fill' as string]: `${Math.round((c.pledgedCents / c.goalCents) * 100)}%`,
              }}
            />
          </Card>
        ))}
      </div>
    </Shot>
  )
}

/* ── The office ───────────────────────────────────────────────────────────── */

export function StaffShot() {
  return (
    <Shot
      crumb="Staff & roles"
      caption="A role says who someone is; what they can reach follows from it."
    >
      <TableCard compact>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Can reach</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_STAFF.map((s) => (
              <tr key={s.name}>
                <td>{s.name}</td>
                <td>
                  <Badge>{s.role}</Badge>
                </td>
                <td>{s.can}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </Shot>
  )
}
